import { RegionShared } from "@1games-ru/region-shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import rpc from "@1games-ru/rage-rpc";
import { AdditionalFlow } from "@/domains/auth/constants/additionalFlow";

const INIT_STATE = {
  [RegionShared.Cef.Events.Auth.AuthSignInShow]: false,
  [RegionShared.Cef.Events.Auth.AuthSignUpShow]: false,
  [RegionShared.Cef.Events.Auth.AuthSignInPinShow]: false,
  [RegionShared.Cef.Events.Auth.AuthCreatePinShow]: false,
  [RegionShared.Cef.Events.Auth.AuthSignIn2FAShow]: false,
  [AdditionalFlow.AuthResetPassword]: false,
  [AdditionalFlow.AuthResetPin]: false,
  [AdditionalFlow.AuthResetTfaAuthenticator]: false,
  [AdditionalFlow.AuthResetTfaEmail]: false,
  [AdditionalFlow.AuthResetTfaSMS]: false,
  [AdditionalFlow.AuthUpdatePassword]: false,
  [AdditionalFlow.AuthUpdatePin]: false,
} as const;

type AuthFlowState = typeof INIT_STATE;
export type AuthFlowKey = keyof AuthFlowState;

type Ctx = {
  state: AuthFlowState;
  isAnyOpen: boolean;
  open: (key: AuthFlowKey) => void;
  openOnly: (key: AuthFlowKey) => void;
  closeAll: () => void;
  goBack: () => void;
  canGoBack: boolean;
  resetHistory: () => void;
  enableTfa: typeof RegionShared.Cef.Requests.AuthSignIn2FAShowRequest;
};

const AuthFlowContext = createContext<Ctx | null>(null);

const showEvents: AuthFlowKey[] = [
  RegionShared.Cef.Events.Auth.AuthCreatePinShow,
  RegionShared.Cef.Events.Auth.AuthSignInShow,
  RegionShared.Cef.Events.Auth.AuthSignInPinShow,
  RegionShared.Cef.Events.Auth.AuthSignUpShow,
];

const hideEvents = [
  RegionShared.Cef.Events.Auth.AuthCreatePinHide,
  RegionShared.Cef.Events.Auth.AuthSignIn2FAHide,
  RegionShared.Cef.Events.Auth.AuthSignInHide,
  RegionShared.Cef.Events.Auth.AuthSignInPinHide,
  RegionShared.Cef.Events.Auth.AuthSignUpHide,
] as const;

type HideEvent = (typeof hideEvents)[number];

const mapHideShowEvents = {
  [RegionShared.Cef.Events.Auth.AuthSignInHide]: RegionShared.Cef.Events.Auth.AuthSignInShow,
  [RegionShared.Cef.Events.Auth.AuthSignUpHide]: RegionShared.Cef.Events.Auth.AuthSignUpShow,
  [RegionShared.Cef.Events.Auth.AuthSignInPinHide]: RegionShared.Cef.Events.Auth.AuthSignInPinShow,
  [RegionShared.Cef.Events.Auth.AuthCreatePinHide]: RegionShared.Cef.Events.Auth.AuthCreatePinShow,
  [RegionShared.Cef.Events.Auth.AuthSignIn2FAHide]: RegionShared.Cef.Events.Auth.AuthSignIn2FAShow,
} as const satisfies Record<HideEvent, AuthFlowKey>;

const MAX_HISTORY = 50;

export function AuthFlowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthFlowState>(INIT_STATE);
  const [enableTfa, setEnableTfa] = useState<
    typeof RegionShared.Cef.Requests.AuthSignIn2FAShowRequest
  >({ isEmailEnabled: false, isGoogleEnabled: false, isSmsEnabled: false });

  const historyRef = useRef<AuthFlowState[]>([]);

  const pushHistory = useCallback((snapshot: AuthFlowState) => {
    historyRef.current.push(snapshot);
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
  }, []);

  const goBack = useCallback(() => {
    const prev = historyRef.current.pop();
    if (prev) {
      setState(prev);
    }
  }, []);

  const resetHistory = useCallback(() => {
    historyRef.current = [];
  }, []);

  const open = useCallback(
    (key: AuthFlowKey) => {
      pushHistory(state);
      setState((s) => ({ ...s, [key]: true }));
    },
    [state, pushHistory],
  );

  const openOnly = useCallback(
    (key: AuthFlowKey) => {
      pushHistory(state);
      setState({ ...INIT_STATE, [key]: true });
    },
    [state, pushHistory],
  );

  const close = useCallback(
    (key: AuthFlowKey) => {
      pushHistory(state);
      setState((s) => ({ ...s, [key]: false }));
    },
    [state, pushHistory],
  );

  const closeAll = useCallback(() => {
    pushHistory(state);
    setState(INIT_STATE);
  }, [state, pushHistory]);

  const isAnyOpen = useMemo(() => Object.values(state).some(Boolean), [state]);
  const canGoBack = historyRef.current.length > 0;

  useEffect(() => {
    showEvents.forEach((event) => {
      rpc.register(event, () => openOnly(event));
    });
    return () => {
      showEvents.forEach((event) => rpc.unregister(event));
    };
  }, [openOnly]);

  useEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.Auth.AuthSignIn2FAShow,
      (data: typeof RegionShared.Cef.Requests.AuthSignIn2FAShowRequest) => {
        setEnableTfa(data);
        openOnly(RegionShared.Cef.Events.Auth.AuthSignIn2FAShow);
      },
    );

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Auth.AuthSignIn2FAShow);
    };
  }, [openOnly]);

  useEffect(() => {
    hideEvents.forEach((event) => {
      rpc.register(event, () => {
        close(mapHideShowEvents[event]);
      });
    });

    return () => {
      hideEvents.forEach((event) => rpc.unregister(event));
    };
  }, [close]);

  const value = useMemo<Ctx>(
    () => ({
      state,
      isAnyOpen,
      open,
      openOnly,
      close,
      closeAll,
      goBack,
      canGoBack,
      resetHistory,
      enableTfa,
    }),
    [state, isAnyOpen, open, openOnly, close, closeAll, goBack, canGoBack, resetHistory, enableTfa],
  );

  return <AuthFlowContext.Provider value={value}>{children}</AuthFlowContext.Provider>;
}

export function useAuthFlow() {
  const ctx = useContext(AuthFlowContext);
  if (!ctx) throw new Error("useAuthFlow must be used within AuthFlowProvider");
  return ctx;
}
